
import XcoinsMoreProducts from "@/components/xcoins/MoreProducts.vue";
import XcoinsStatusDialog from "@/components/xcoins/StatusDialog.vue";
import {
  canPurchaseAtSite,
  formatPoints,
  getCoverUrl,
  getPrimaryGoodsDetail,
  getProductDescription,
  getProductPoints,
  getProductTitle,
  getDeliveryNote,
  getSpecText,
  isPointsApiSuccess,
  isProductOutOfStock,
  normalizeMallGoodsList,
} from "@/utils/mallGoods";
import { ensureMallSite } from "@/utils/mallSite";
import {
  getPointsApiErrorBody,
  resolveMallRedeemError,
  usesRedeemErrorMsgTip,
} from "@/utils/mallRedeemErrors";

export default {
  name: "XcoinsGoodsDetail",
  components: { XcoinsMoreProducts, XcoinsStatusDialog },
  layout: "DefaultLayout",
  head() {
    return {
      title: this.productTitle || this.$t("xcoins.XCOINS_SHOP"),
    };
  },
  data() {
    return {
      product: null,
      userPoints: 0,
      pointsReady: false,
      pageLoading: true,
      loadError: false,
      coverFailed: false,
      submitting: false,
      redeemBlockKind: "", // unavailable / outOfStock / insufficientPoints / stationNotMatch / purchaseLimit
      redeemBlockMsg: "", // 业务失败页内提示只认接口 res.msg
      statusDialog: {
        visible: false,
        type: "error",
        title: "",
        message: "",
        primaryText: "",
        secondaryText: "",
        primaryAction: "",
      },
    };
  },
  computed: {
    goodsId() {
      return this.$route.params.id;
    },
    productTitle() {
      return this.product ? getProductTitle(this.product) : "-";
    },
    productDesc() {
      return this.product ? getProductDescription(this.product) : "";
    },
    productPoints() {
      return this.product ? getProductPoints(this.product) : 0;
    },
    specText() {
      return this.product ? getSpecText(this.product) : "";
    },
    deliveryNote() {
      return this.product ? getDeliveryNote(this.product) : "";
    },
    coverUrl() {
      return this.product ? getCoverUrl(this.product) : "";
    },
    inStock() {
      return this.product && !isProductOutOfStock(this.product);
    },
    siteSupported() {
      return this.product ? canPurchaseAtSite(this.product) : false;
    },
    /** 已登录且余额已就绪时，余额小于商品积分视为不足；未登录不据此置灰，以便点兑换去登录 */
    isInsufficientPoints() {
      if (this.redeemBlockKind === "insufficientPoints") {
        return true;
      }
      return Boolean(
        this.$store.state.isLogin &&
        this.pointsReady &&
        this.userPoints < this.productPoints
      );
    },
    canClickRedeem() {
      return this.siteSupported && this.inStock && !this.submitting && !this.isInsufficientPoints && this.redeemBlockKind !== "purchaseLimit";
    },
    redeemButtonText() {
      if (!this.siteSupported || this.redeemBlockKind === "purchaseLimit") {
        return this.$t("xcoins.UNAVAILABLE_REDEEM");
      }
      if (!this.inStock) {
        return this.$t("xcoins.RESTOCKING");
      }
      return this.$t("xcoins.REDEEM_NOW");
    },
    actionTip() {
      if (!this.product) {
        return "";
      }
      if (usesRedeemErrorMsgTip(this.redeemBlockKind) && this.redeemBlockMsg) {
        return this.redeemBlockMsg;
      }
      if (!this.siteSupported) {
        return this.$t("xcoins.SITE_NOT_SUPPORTED");
      }
      if (this.isInsufficientPoints) {
        return this.$t("xcoins.INSUFFICIENT_TO_REDEEM");
      }
      return "";
    },
  },
  watch: {
    "$route.params.id"() {
      this.loadPage();
    },
    "$store.state.isLogin": {
      async handler(loggedIn) {
        if (!loggedIn) {
          this.userPoints = 0;
          this.pointsReady = true;
          return;
        }

        // 登录弹窗不会重新创建当前页面；已有积分 token 时只刷新余额，避免重复 /point/login 覆盖有效 token。
        this.pointsReady = false;
        if (!this.$store.state.pointToken) {
          this.$store.commit("CLEAR_POINT_CACHE");
        }
        await this.fetchUserPoints();
      },
    },
  },
  mounted() {
    this.loadPage();
  },
  methods: {
    formatPoints,
    goBack() {
      if (process.client && window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push("/xcoins/exchange");
    },
    async loadPage() {
      this.pageLoading = true;
      this.loadError = false;
      this.coverFailed = false;
      this.product = null;
      this.redeemBlockKind = "";
      this.redeemBlockMsg = "";
      this.closeStatusDialog();
      try {
        // 站点只看访问者：本地手动选择 > IP > 无站点，不用商品自身站点覆盖。
        await ensureMallSite(this.$cookies);
        await this.fetchGoods();
        await this.fetchUserPoints();
      } catch (e) {
        this.loadError = true;
      } finally {
        this.pageLoading = false;
      }
    },
    async fetchGoods() {
      if (!this.goodsId) {
        this.loadError = true;
        return;
      }

      const res = await this.$global.pointsMallGoodsDetail(this.goodsId).catch(() => null);
      if (!isPointsApiSuccess(res)) {
        this.loadError = true;
        return;
      }

      const payload = res.data;
      this.product = payload && payload.id
        ? normalizeMallGoodsList([payload])[0] || null
        : null;

      if (!this.product) {
        this.loadError = true;
        this.$nextTick(() => {
          this.openUnavailableDialog();
        });
        return;
      }

      if (!this.siteSupported) {
        this.$nextTick(() => {
          this.openUnavailableDialog();
        });
      }
    },
    async fetchUserPoints() {
      if (!this.$store.state.isLogin) {
        this.userPoints = 0;
        this.pointsReady = true;
        return;
      }
      const pointDetail = await this.$pointsCache.ensurePointDetail();
      this.userPoints = this.parsePointValue(pointDetail?.points);
      this.pointsReady = Boolean(this.$store.state.pointDetailLoaded);
    },
    parsePointValue(value) {
      return Number(String(value || 0).replace(/,/g, "")) || 0;
    },
    openLogin() {
      const useNewLogin =
        this.$store.state.siteConfig &&
        this.$store.state.siteConfig.DialogPassportSwitch == "1";
      if (useNewLogin) {
        this.$store.commit("OPEN_AUTH_MODAL", {
          tab: "login",
          redirectToUserCenter: false,
          source: {
            elementTypeName: "兑换商品",
            clickName: "login",
            loginChannel: "兑换商品",
          },
        });
      } else {
        this.$jumpTerminus(1);
      }
    },
    getRedeemBlockReason() {
      if (!this.product) {
        return this.$t("page.NETWORK_ERROR");
      }
      if (!this.siteSupported) {
        return this.$t("xcoins.SITE_NOT_SUPPORTED");
      }
      if (!this.inStock) {
        return this.$t("xcoins.RESTOCKING");
      }
      if (this.isInsufficientPoints) {
        return this.$t("xcoins.INSUFFICIENT_TO_REDEEM");
      }
      return "";
    },
    closeStatusDialog() {
      this.statusDialog.visible = false;
    },
    openStatusDialog(options = {}) {
      this.statusDialog = {
        visible: true,
        type: options.type || "error",
        title: options.title || "",
        message: options.message || "",
        primaryText: options.primaryText || this.$t("xcoins.I_KNOW"),
        secondaryText: options.secondaryText || "",
        primaryAction: options.primaryAction || "",
      };
    },
    /** 当前站点不可兑时的提示弹窗 */
    openUnavailableDialog() {
      this.openStatusDialog({
        type: "warning",
        title: this.$t("xcoins.UNAVAILABLE_REDEEM"),
        message: this.$t("xcoins.SITE_NOT_SUPPORTED"),
        secondaryText: this.$t("page.CANCEL"),
        primaryText: this.$t("xcoins.BACK_TO_SHOP"),
        primaryAction: "backToShop",
      });
    },
    /** 兑换预览失败：错误态 StatusDialog，不进确认页 */
    openPreviewFailedDialog(message) {
      this.openStatusDialog({
        type: "error",
        title: this.$t("xcoins.REDEEM_FAILED"),
        message: message || this.$t("page.REDEMPTION_FAILED"),
        primaryText: this.$t("xcoins.I_KNOW"),
      });
    },
    /** 按兑换业务码改库存/站点/积分态，弹窗文案只认 res.msg */
    applyRedeemErrorFromRes(res) {
      const { kind, product, message } = resolveMallRedeemError(res, this.product);
      if (!kind) {
        return "";
      }
      this.redeemBlockKind = kind;
      this.redeemBlockMsg = message;
      if (product) {
        this.product = product;
      }
      return kind;
    },
    /** 下架/站点不匹配走不可兑弹窗；库存/积分走兑换失败弹窗 */
    openRedeemErrorDialog(res, kind) {
      const message = (res && res.msg) || this.$t("page.REDEMPTION_FAILED");
      if (kind === "stationNotMatch" || kind === "unavailable") {
        this.openStatusDialog({
          type: "warning",
          title: this.$t("xcoins.UNAVAILABLE_REDEEM"),
          message,
          secondaryText: this.$t("page.CANCEL"),
          primaryText: this.$t("xcoins.BACK_TO_SHOP"),
          primaryAction: "backToShop",
        });
        return;
      }
      this.openPreviewFailedDialog(message);
    },
    handleStatusPrimary() {
      const action = this.statusDialog.primaryAction;
      this.closeStatusDialog();
      if (action === "backToShop") {
        this.$router.push("/xcoins/exchange");
      }
    },
    /** 兑换预览请求体，goodsId / detailId 只传非空字符串 */
    buildPreviewPayload(detail = {}) {
      return {
        detailId: String(detail.id || "").trim(),
        goodsId: String(detail.goodsId || (this.product && this.product.id) || "").trim(),
      };
    },
    async handleRedeem() {
      if (this.submitting) {
        return;
      }
      if (!this.siteSupported) {
        this.openUnavailableDialog();
        return;
      }
      if (!this.$store.state.isLogin) {
        this.openLogin();
        return;
      }
      if (this.isInsufficientPoints) {
        return;
      }

      // 点击兑换只走 preview，不请求 /points/detail；余额以 preview 为准
      const reason = this.getRedeemBlockReason();
      if (reason) {
        this.$message.error(reason);
        return;
      }

      const detail = getPrimaryGoodsDetail(this.product);
      const payload = this.buildPreviewPayload(detail);
      if (!payload.detailId || !payload.goodsId) {
        this.openPreviewFailedDialog(this.$t("page.REDEMPTION_FAILED"));
        return;
      }

      this.submitting = true;
      try {
        let res = null;
        try {
          res = await this.$pointsCache.requestWithPointToken((token) => {
            return this.$global.pointsMallExchangePreview(payload, token);
          });
        } catch (e) {
          res = getPointsApiErrorBody(e);
        }

        // 积分商城成功码只认字符串 "200"；失败弹错误弹窗并同步页内状态
        if (!isPointsApiSuccess(res)) {
          const kind = this.applyRedeemErrorFromRes(res);
          this.openRedeemErrorDialog(res, kind);
          return;
        }

        // 余额不足只认 preview.sufficient === false
        if (res.data && res.data.sufficient === false) {
          this.redeemBlockKind = "insufficientPoints";
          this.openPreviewFailedDialog(this.$t("page.INSUFFICIENT_XCOINS"));
          return;
        }

        this.$router.push({
          path: "/xcoins/orderConfirm",
          query: {
            id: this.product.id,
            detailId: payload.detailId,
          },
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
